import {
  SAFETY_REPORT_CATEGORIES,
  type SafetyReportCategory,
  type SafetyReportDetail,
} from "@wallyo/contracts/console/safety";
import { useRef, useState } from "react";

import { openSafetyReport } from "../../api/console-api";
import { useApi } from "../../api/context";
import { createIntent } from "../../api/idempotency";
import { t } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { CustomerPicker } from "../cases/CaseDialogs";
import { Modal } from "../team/TeamDialogs";

/**
 * Open a report from the console (WALLIYO-0122).
 *
 * The app has a "report this person" button; the console did not, so a
 * complaint that arrived by phone — which in Guinea is how most of them will
 * arrive — had nowhere to go. The endpoint has existed since the safety work
 * and had no caller.
 *
 * `source` is `phone` or `console`: never `app`, which is reserved for a report
 * a customer actually submitted themselves. The contract enforces that; this
 * offers the two that are ours.
 */

type Fields = {
  subjectUserId: string;
  reporterUserId: string;
  source: "phone" | "console";
  category: SafetyReportCategory;
  description: string;
};

const EMPTY: Fields = {
  subjectUserId: "",
  reporterUserId: "",
  source: "phone",
  category: "harassment",
  description: "",
};

export function newReportProblem(fields: Fields): string | null {
  if (!fields.subjectUserId.trim()) {
    return t("reports.new.needSubject");
  }
  if (fields.description.trim().length < 10) {
    return t("reports.new.needDescription");
  }
  return null;
}

export function NewReportDialog({
  canSearch,
  onClose,
  onOpened,
}: {
  canSearch: boolean;
  onClose: () => void;
  onOpened: (report: SafetyReportDetail) => void;
}) {
  const { client } = useApi();
  const intent = useRef(createIntent());
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const set = (change: Partial<Fields>) => {
    intent.current.reset();
    setProblem(null);
    setFields((current) => ({ ...current, ...change }));
  };

  async function submit() {
    const bad = newReportProblem(fields);
    setProblem(bad);
    if (bad) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      const { safetyReport } = await openSafetyReport(
        client,
        {
          subjectUserId: fields.subjectUserId.trim(),
          // Blank means we do not know who reported it — someone calling about
          // a third party, or staff noticing it themselves.
          reporterUserId: fields.reporterUserId.trim() || null,
          source: fields.source,
          category: fields.category,
          description: fields.description.trim(),
        },
        { idempotencyKey: intent.current.key() },
      );
      intent.current.reset();
      onOpened(safetyReport);
    } catch (failure) {
      setError(failure);
      setPending(false);
    }
  }

  return (
    <Modal title={t("reports.new.title")} onClose={onClose}>
      <fieldset className="reply-channels">
        <legend>{t("reports.new.source")}</legend>
        <label>
          <input
            type="radio"
            name="report-source"
            checked={fields.source === "phone"}
            onChange={() => set({ source: "phone" })}
          />{" "}
          {t("reports.new.byPhone")}
        </label>
        <label>
          <input
            type="radio"
            name="report-source"
            checked={fields.source === "console"}
            onChange={() => set({ source: "console" })}
          />{" "}
          {t("reports.new.byStaff")}
        </label>
      </fieldset>

      <p className="meta">{t("reports.new.subject")}</p>
      <CustomerPicker
        canSearch={canSearch}
        userId={fields.subjectUserId}
        onPick={(subjectUserId) => set({ subjectUserId })}
      />

      <div className="field">
        <label htmlFor="report-reporter">{t("reports.new.reporter")}</label>
        <input
          id="report-reporter"
          className="mono"
          dir="ltr"
          value={fields.reporterUserId}
          onChange={(event) => set({ reporterUserId: event.target.value })}
        />
        <span className="meta">{t("reports.new.reporterHint")}</span>
      </div>

      <div className="field">
        <label htmlFor="report-category">{t("reports.new.category")}</label>
        <select
          id="report-category"
          value={fields.category}
          onChange={(event) => set({ category: event.target.value as SafetyReportCategory })}
        >
          {SAFETY_REPORT_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {t(`reports.category.${category}` as const)}
            </option>
          ))}
        </select>
        {/* The severity follows from the category unless staff change it later,
            so it is not asked for here: one fewer decision on a phone call. */}
        <span className="meta">{t("reports.new.categoryHint")}</span>
      </div>

      <div className="field">
        <label htmlFor="report-description">{t("reports.new.description")}</label>
        <textarea
          id="report-description"
          rows={4}
          value={fields.description}
          onChange={(event) => set({ description: event.target.value })}
        />
        <span className="meta">{t("reports.new.descriptionHint")}</span>
      </div>

      {problem ? <p className="banner warn">{problem}</p> : null}
      {error ? <ErrorNotice error={error} /> : null}

      <div className="btns">
        <button className="btn" type="button" onClick={onClose} disabled={pending}>
          {t("team.cancel")}
        </button>
        <button className="btn primary" type="button" onClick={() => void submit()} disabled={pending}>
          {pending ? t("team.sending") : t("reports.new.submit")}
        </button>
      </div>
    </Modal>
  );
}
